import classNames from 'classnames';
import * as React from 'react';
import { AutoSizer, Dimensions } from 'react-virtualized';

import { Bem } from '../../service/Bem/Bem';
import { KeyCode } from '../../typings/common';
import { Cell, Column, Table } from '../TableDecorator/TableDecorator';

import './SelectionList.css';

interface SelectionListProps {
    onSelectionChange?: (
        indexOfClick: number,
        indexOfPreviousClick: number,
        keyCode?: KeyCode
    ) => void;
    columns: SelectionListColumn[];
    rowsCount: number;
    cls?: string;
    rowClassNameGetter?: (index: number) => string;
    rowHeight: number;
    tableHeight?: number;
    tableMaxHeight?: number;
    headerHeight?: number;
    hasBorders?: boolean;
    onScrollStart?: () => void;
    scrollToRow?: number;
}

interface SelectionListState {
    indexOfLastClick: number;
}

export interface SelectionListColumn {
    getCellContent: (rowIndex: number) => JSX.Element;
    header?: () => JSX.Element;
    width?: number;
    flexGrow?: number;
}

export class SelectionList extends React.PureComponent<
    SelectionListProps,
    SelectionListState
> {
    constructor(props: SelectionListProps) {
        super(props);

        this.state = {
            indexOfLastClick: 0
        };
    }

    render() {
        const { cls, tableHeight } = this.props;

        return (
            <div
                className={classNames(cls, Bem.cls('selection-list'))}
                style={{ height: tableHeight }}
            >
                {this.getContent()}
            </div>
        );
    }

    private getContent() {
        const {
            columns,
            rowClassNameGetter,
            rowsCount,
            rowHeight,
            headerHeight = 0,
            hasBorders,
            onScrollStart,
            scrollToRow
        } = this.props;

        return (
            <AutoSizer>
                {({ width, height }: Dimensions) => {
                    return (
                        <Table
                            onScrollStart={onScrollStart}
                            isColumnResizing={false}
                            width={width}
                            height={height}
                            className={Bem.cls('selection-list', 'list', {
                                'has-border': hasBorders
                            })}
                            headerHeight={headerHeight}
                            rowHeight={rowHeight}
                            rowsCount={rowsCount}
                            bufferRowCount={7}
                            rowClassNameGetter={rowClassNameGetter}
                            onRowClick={this.onRowClick}
                            showScrollbarX={false}
                            overflowX={'hidden'}
                            rowKeyGetter={index => `row-${String(index)}`}
                            scrollToRow={scrollToRow}
                        >
                            {columns.map((el, index) => {
                                return (
                                    <Column
                                        key={index}
                                        header={el.header}
                                        cell={({ rowIndex }) => (
                                            <Cell>
                                                {el.getCellContent(rowIndex)}
                                            </Cell>
                                        )}
                                        width={el.width || 0}
                                        flexGrow={el.flexGrow}
                                    />
                                );
                            })}
                        </Table>
                    );
                }}
            </AutoSizer>
        );
    }

    private onRowClick = (event: React.MouseEvent<Table>, rowIndex: number) => {
        const { onSelectionChange } = this.props;

        if (!onSelectionChange) {
            return;
        }

        let keyCode;

        if (event.shiftKey) {
            keyCode = KeyCode.SHIFT;
        }

        if (event.ctrlKey || event.metaKey) {
            keyCode = KeyCode.CTRL;
        }

        onSelectionChange(rowIndex, this.state.indexOfLastClick, keyCode);

        if (!keyCode) {
            this.setState({
                indexOfLastClick: rowIndex
            });
        }
    };
}
